/**
 * Discussion Service - API for creating and managing governance discussions
 *
 * Story FOS-4.3.1a: Core Conversion Flow
 * Task 6: Add governance-suite API endpoint
 *
 * Client for governance discussion operations including creating discussions
 * from think-tank captures.
 *
 * @see AC-3 - Confirmation dialog on action
 * @see AC-4 - API call to governance-suite
 * @see AC-6 - Bidirectional linking
 *
 * @module services/discussionService
 */

import { authenticatedFetch } from '@hello-world-dao/auth';
import {
  // bl-1552: the Bearer-attaching wrapper (injects the identity-service JWT from
  // the in-memory identity-jwt-store). The requireSessionAuth-gated contributors/
  // stage calls below use THIS, not the bare cookie-only `authenticatedFetch`
  // above, so a cold-boot/deep-link request carries the Bearer and does not 401.
  authenticatedFetchWithBearer,
  getApiBaseUrl,
  getOracleBridgeUrl,
  createTimeout as createTimeoutBase,
  DEFAULT_TIMEOUT_MS,
  readErrorBody,
  readStringField,
  selectServerMessage,
} from './apiHelpers';
// bl-1552: single-flight pre-mount session bootstrap (?code= exchange / silent
// /token/refresh). Awaiting it before a requireSessionAuth read guarantees the
// identity-jwt-store is populated (or a definitive verdict reached) so a deep-link
// cannot race the auth-gated fetch ahead of the Bearer. It is cached + idempotent,
// so awaiting the already-settled promise after the initial resolve is a no-op.
import { runIdentityCodeBootstrap } from './identity-code-bootstrap';
import type { DiscussionCategory, DiscussionStage } from '../stores/discussionStore';
import type { DiscussionSummary } from '../components/governance/DiscussionList';

// =============================================================================
// Types
// =============================================================================

/**
 * Request to create a discussion from a capture
 */
export interface CreateFromCaptureRequest {
  /** Original capture ID from think-tank */
  captureId: string;
  /** Discussion title (from capture title) */
  title: string;
  /** Discussion description (from capture content/description) */
  description: string;
  /** Optional category for the discussion */
  category?: string;
  /** Optional tags from the capture */
  tags?: string[];
  /** User principal who initiated the conversion */
  initiatorId: string;
  /** Idempotency key to prevent duplicate creations */
  idempotencyKey?: string;
}

/**
 * Response from creating a discussion
 */
export interface CreateFromCaptureResponse {
  /** Created discussion ID */
  discussionId: string;
  /** Relative URL to the discussion */
  discussionUrl: string;
  /** Whether a new discussion was created (false if idempotent duplicate) */
  created: boolean;
}

/**
 * Lookup response for finding discussion by capture ID
 */
export interface DiscussionLookupResponse {
  /** Whether a discussion exists for this capture */
  exists: boolean;
  /** Discussion ID if exists */
  discussionId?: string;
  /** Discussion URL if exists */
  discussionUrl?: string;
}

/**
 * Discussion details
 */
export interface Discussion {
  id: string;
  title: string;
  description: string;
  category?: string;
  tags: string[];
  createdBy: string;
  createdAt: number;
  updatedAt: number;
  stage: 'Brainstorm' | 'Refining' | 'Ready';
  commentCount: number;
  participantCount: number;
  /** Linked capture ID from think-tank (if originated from capture) */
  sourceCaptureId?: string;
}

/**
 * Discussion error types
 */
export enum DiscussionErrorType {
  /** Input validation failed */
  VALIDATION_ERROR = 'validation_error',
  /** Not authorized to perform action */
  UNAUTHORIZED = 'unauthorized',
  /** Request rejected by backend */
  REJECTED = 'rejected',
  /** Discussion already exists for this capture */
  DUPLICATE = 'duplicate',
  /** Resource not found */
  NOT_FOUND = 'not_found',
  /** Server error */
  SERVER_ERROR = 'server_error',
  /** Network error */
  NETWORK_ERROR = 'network_error',
  /** Request timeout */
  TIMEOUT = 'timeout',
}

/**
 * Discussion error with typed info
 */
export interface DiscussionError {
  type: DiscussionErrorType;
  message: string;
  existingDiscussionId?: string;
}

/**
 * Options for API calls
 */
export interface RequestOptions {
  /** Request timeout in milliseconds (default: 30000) */
  timeoutMs?: number;
}

// =============================================================================
// Constants
// =============================================================================

const MIN_TITLE_LENGTH = 1;
const MAX_TITLE_LENGTH = 200;

// =============================================================================
// Helpers
// =============================================================================

/**
 * Create a timeout promise with DiscussionError type
 */
function createTimeout(ms: number): Promise<never> {
  return createTimeoutBase<DiscussionError>(ms, DiscussionErrorType.TIMEOUT);
}

/**
 * Validate create from capture request
 * @throws DiscussionError if validation fails
 */
function validateCreateFromCaptureRequest(request: CreateFromCaptureRequest): void {
  // Validate captureId
  if (!request.captureId || request.captureId.trim().length === 0) {
    const error: DiscussionError = {
      type: DiscussionErrorType.VALIDATION_ERROR,
      message: 'Capture ID is required',
    };
    throw error;
  }

  // Validate title
  if (!request.title || request.title.length < MIN_TITLE_LENGTH) {
    const error: DiscussionError = {
      type: DiscussionErrorType.VALIDATION_ERROR,
      message: `Title must be at least ${MIN_TITLE_LENGTH} character(s)`,
    };
    throw error;
  }
  if (request.title.length > MAX_TITLE_LENGTH) {
    const error: DiscussionError = {
      type: DiscussionErrorType.VALIDATION_ERROR,
      message: `Title must be at most ${MAX_TITLE_LENGTH} characters`,
    };
    throw error;
  }

  // Validate initiatorId
  if (!request.initiatorId || request.initiatorId.trim().length === 0) {
    const error: DiscussionError = {
      type: DiscussionErrorType.VALIDATION_ERROR,
      message: 'Initiator ID is required',
    };
    throw error;
  }
}

/**
 * Handle API error response
 *
 * bl-1153-1: the body is read EXACTLY ONCE at the top and every status branch
 * prefers the server's own sentence over the canned string. Previously only the
 * 409 and 400/422 branches read the body at all, and the 400/422 branch inverted
 * the precedence (`data.error || data.message`) against a server that puts the
 * machine code in `error` — a 400 from `POST /api/discussions/from-capture`
 * rendered the token `validation_error` instead of `captureId is required`.
 *
 * The error TYPE per status is unchanged. `DiscussionDetailPage` branches on
 * `type`/`code`, so a change there would silently be a navigation change.
 *
 * @throws DiscussionError
 */
async function handleErrorResponse(response: Response): Promise<never> {
  const status = response.status;
  const data = await readErrorBody(response);
  const serverMessage = selectServerMessage(data);

  if (status === 401 || status === 403) {
    const error: DiscussionError = {
      type: DiscussionErrorType.UNAUTHORIZED,
      message: serverMessage ?? 'Not authorized to perform this action',
    };
    throw error;
  }

  if (status === 404) {
    const error: DiscussionError = {
      type: DiscussionErrorType.NOT_FOUND,
      message: serverMessage ?? 'Resource not found',
    };
    throw error;
  }

  if (status === 409) {
    const error: DiscussionError = {
      type: DiscussionErrorType.DUPLICATE,
      message: serverMessage ?? 'A discussion already exists for this capture',
      existingDiscussionId: readStringField(data, 'existingDiscussionId'),
    };
    throw error;
  }

  if (status === 400 || status === 422) {
    const error: DiscussionError = {
      type: DiscussionErrorType.REJECTED,
      message: serverMessage ?? 'Request validation failed',
    };
    throw error;
  }

  if (status >= 500) {
    const error: DiscussionError = {
      type: DiscussionErrorType.SERVER_ERROR,
      message: serverMessage ?? `Server error: ${status} ${response.statusText}`,
    };
    throw error;
  }

  // Generic error
  const error: DiscussionError = {
    type: DiscussionErrorType.REJECTED,
    message: serverMessage ?? `Request failed: ${status} ${response.statusText}`,
  };
  throw error;
}

// =============================================================================
// API Functions
// =============================================================================

/**
 * Create a new discussion from a think-tank capture
 *
 * Creates a governance discussion with bidirectional linking to the source capture.
 * Uses idempotency key to prevent duplicate creations on retry.
 *
 * @param request - Discussion creation request with capture data
 * @param options - Request options (timeout, etc.)
 * @returns Promise resolving to the created discussion details
 * @throws DiscussionError on validation or creation failure
 *
 * @see AC-4 - API call to governance-suite
 * @see AC-6 - Bidirectional linking
 */
export async function createFromCapture(
  request: CreateFromCaptureRequest,
  options: RequestOptions = {}
): Promise<CreateFromCaptureResponse> {
  const { timeoutMs = DEFAULT_TIMEOUT_MS } = options;

  // Validate inputs before making request
  validateCreateFromCaptureRequest(request);

  const url = `${getApiBaseUrl()}/api/discussions/from-capture`;

  try {
    const fetchPromise = authenticatedFetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    const response = await Promise.race([fetchPromise, createTimeout(timeoutMs)]);

    if (!response.ok) {
      await handleErrorResponse(response);
    }

    const data = await response.json();
    return data as CreateFromCaptureResponse;
  } catch (e) {
    // Re-throw DiscussionError
    if (isDiscussionError(e)) {
      throw e;
    }

    // Wrap other errors
    const error: DiscussionError = {
      type: DiscussionErrorType.NETWORK_ERROR,
      message: e instanceof Error ? e.message : 'Network error occurred',
    };
    throw error;
  }
}

/**
 * Look up a discussion by its source capture ID
 *
 * Used for fallback sync when WebSocket confirmation is missed.
 *
 * @param captureId - The capture ID to look up
 * @param options - Request options
 * @returns Promise resolving to lookup result
 * @throws DiscussionError on failure
 */
export async function getByCapture(
  captureId: string,
  options: RequestOptions = {}
): Promise<DiscussionLookupResponse> {
  const { timeoutMs = DEFAULT_TIMEOUT_MS } = options;

  const url = `${getApiBaseUrl()}/api/discussions/by-capture/${encodeURIComponent(captureId)}`;

  try {
    const fetchPromise = authenticatedFetch(url, {
      method: 'GET',
    });

    const response = await Promise.race([fetchPromise, createTimeout(timeoutMs)]);

    if (!response.ok) {
      if (response.status === 404) {
        return { exists: false };
      }
      await handleErrorResponse(response);
    }

    const data = await response.json();
    return data as DiscussionLookupResponse;
  } catch (e) {
    // Re-throw DiscussionError
    if (isDiscussionError(e)) {
      throw e;
    }

    // Return not found on network errors (graceful degradation)
    return { exists: false };
  }
}

/**
 * Get a discussion by ID
 *
 * @param discussionId - The discussion ID to retrieve
 * @param options - Request options
 * @returns Promise resolving to the discussion details
 * @throws DiscussionError on failure
 */
export async function getDiscussion(
  discussionId: string,
  options: RequestOptions = {}
): Promise<Discussion> {
  const { timeoutMs = DEFAULT_TIMEOUT_MS } = options;

  const url = `${getApiBaseUrl()}/api/discussions/${encodeURIComponent(discussionId)}`;

  try {
    const fetchPromise = authenticatedFetch(url, {
      method: 'GET',
    });

    const response = await Promise.race([fetchPromise, createTimeout(timeoutMs)]);

    if (!response.ok) {
      await handleErrorResponse(response);
    }

    const data = await response.json();
    return data as Discussion;
  } catch (e) {
    // Re-throw DiscussionError
    if (isDiscussionError(e)) {
      throw e;
    }

    // Wrap other errors
    const error: DiscussionError = {
      type: DiscussionErrorType.NETWORK_ERROR,
      message: e instanceof Error ? e.message : 'Network error occurred',
    };
    throw error;
  }
}

// =============================================================================
// Type Guard
// =============================================================================

/**
 * Type guard for DiscussionError
 */
export function isDiscussionError(error: unknown): error is DiscussionError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'type' in error &&
    'message' in error &&
    Object.values(DiscussionErrorType).includes((error as DiscussionError).type)
  );
}

// =============================================================================
// Governance-Native Discussion Types (BL-039.1)
// =============================================================================

/**
 * Discussion as returned by oracle-bridge GET /api/discussions
 */
export interface GovernanceDiscussion {
  id: string;
  title: string;
  description: string | null;
  category: string | null;
  tags: string[];
  createdBy: string;
  /** Display name for the creator (may not be present in older API responses) */
  createdByName?: string;
  createdAt: number;
  updatedAt: number;
  stage: DiscussionStage;
  commentCount: number;
  participantCount: number;
  sourceCaptureId: string | null;
}

/**
 * Response from GET /api/discussions
 */
export interface GovernanceDiscussionListResponse {
  discussions: GovernanceDiscussion[];
  total: number;
}

/**
 * Data for creating a new discussion directly (governance-native)
 */
export interface GovernanceCreateDiscussionData {
  title: string;
  category: string;
  description?: string;
}

/**
 * Response from POST /api/discussions
 */
export interface GovernanceCreateDiscussionResponse {
  discussionId: string;
  discussionUrl: string;
  created: boolean;
}

// =============================================================================
// Governance-Native Discussion API Error Handling (BL-039.1)
// =============================================================================

export interface GovernanceDiscussionApiError {
  code: 'UNAUTHENTICATED' | 'VALIDATION_ERROR' | 'NOT_FOUND' | 'SERVER_ERROR';
  status?: number;
  message?: string;
}

/**
 * Map an error `Response` from the governance-native discussion API.
 *
 * bl-1153-1: this is the only sibling mapper on a LIVE mounted path
 * (`DiscussionsPage`, `DiscussionDetailPage`). Before this story only the
 * 400/422 branch read the body, so a 401, 404 or 5xx carrying a real explanation
 * threw `{ code }` with no `message` at all and the page substituted a hardcoded
 * sentence. The body is now read exactly once and every branch prefers the
 * server's text.
 *
 * `message` is left UNDEFINED when the server supplied no human text, exactly as
 * before. That is deliberate: the render sites own the curated per-context copy
 * ("Discussion not found" reads better than any generic string this layer could
 * invent), so this mapper upgrades them with the server's sentence when there is
 * one and otherwise gets out of the way. Member-facing wording is Menley's lane.
 *
 * Codes and statuses are byte-identical to the pre-bl-1153-1 mapping — the UI
 * routes on `code` (`DiscussionDetailPage.tsx` NOT_FOUND / UNAUTHENTICATED).
 */
async function mapGovernanceError(res: Response): Promise<GovernanceDiscussionApiError> {
  const data = await readErrorBody(res);
  const serverMessage = selectServerMessage(data);

  if (res.status === 401) {
    return { code: 'UNAUTHENTICATED', message: serverMessage };
  }
  if (res.status === 400 || res.status === 422) {
    // The only branch that carried a canned string before bl-1153-1; it stays.
    return {
      code: 'VALIDATION_ERROR',
      status: res.status,
      message: serverMessage ?? 'Validation failed',
    };
  }
  if (res.status === 404) {
    return { code: 'NOT_FOUND', status: 404, message: serverMessage };
  }
  return { code: 'SERVER_ERROR', status: res.status, message: serverMessage };
}

// =============================================================================
// Governance-Native Discussion Mapping (BL-039.1)
// =============================================================================

/**
 * Format a user ID for display when no display name is available.
 * Truncates UUID to first 8 characters or returns "Unknown" for empty values.
 * BL-105 / BUG-DAO-008: Never show raw UUIDs in the UI.
 */
export function formatUserIdFallback(userId: string): string {
  if (!userId || userId.trim().length === 0) return 'Unknown';
  // If it looks like a UUID (contains dashes, 36 chars), truncate to first segment
  if (userId.length >= 36 && userId.includes('-')) {
    return userId.split('-')[0] + '...';
  }
  // For medium-length strings with dashes (13-35 chars), show as-is if under 20 chars
  if (userId.length >= 13 && userId.length <= 35 && userId.includes('-')) {
    if (userId.length < 20) return userId;
    return userId.slice(0, 8) + '...';
  }
  // For short IDs, show as-is
  if (userId.length <= 12) return userId;
  // For other long strings, truncate
  return userId.slice(0, 8) + '...';
}

/**
 * Map GovernanceDiscussion to DiscussionSummary for DiscussionList component.
 *
 * Field mapping:
 * - category defaults to 'Operational' if null
 * - proposerName: uses createdByName if available, falls back to truncated ID (BUG-DAO-008)
 * - lastActivityAt uses updatedAt
 */
export function mapToDiscussionSummary(d: GovernanceDiscussion): DiscussionSummary {
  return {
    id: d.id,
    title: d.title,
    category: (d.category as DiscussionCategory) || 'Operational',
    stage: d.stage,
    proposerId: d.createdBy,
    proposerName: d.createdByName || formatUserIdFallback(d.createdBy),
    participantCount: d.participantCount,
    commentCount: d.commentCount,
    createdAt: d.createdAt,
    lastActivityAt: d.updatedAt,
  };
}

// =============================================================================
// Governance-Native Discussion API Functions (BL-039.1)
// =============================================================================

/**
 * List governance discussions from oracle-bridge.
 * Uses cookie-based session auth (credentials: 'include').
 *
 * CRITICAL: Uses getOracleBridgeUrl(), NOT getApiBaseUrl().
 *
 * @returns Array of GovernanceDiscussion objects
 * @throws GovernanceDiscussionApiError on failure
 */
export async function listGovernanceDiscussions(): Promise<GovernanceDiscussion[]> {
  const base = getOracleBridgeUrl();
  const res = await authenticatedFetch(`${base}/api/discussions`, {
  });
  if (!res.ok) throw await mapGovernanceError(res);
  const data: GovernanceDiscussionListResponse = await res.json();
  return data.discussions;
}

/**
 * Create a new governance discussion via oracle-bridge.
 * Uses cookie-based session auth (credentials: 'include').
 *
 * CRITICAL: Uses getOracleBridgeUrl(), NOT getApiBaseUrl().
 *
 * @param data - Title, category, and optional description
 * @returns Created discussion response with discussionId
 * @throws GovernanceDiscussionApiError on failure
 */
export async function createGovernanceDiscussion(
  data: GovernanceCreateDiscussionData,
): Promise<GovernanceCreateDiscussionResponse> {
  const base = getOracleBridgeUrl();
  const res = await authenticatedFetch(`${base}/api/discussions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      title: data.title,
      category: data.category,
      ...(data.description ? { description: data.description } : {}),
    }),
  });
  if (!res.ok) throw await mapGovernanceError(res);
  return res.json();
}

/**
 * Get a single governance discussion by ID from oracle-bridge.
 * Uses cookie-based session auth (credentials: 'include').
 *
 * CRITICAL: Uses getOracleBridgeUrl(), NOT getApiBaseUrl().
 *
 * @param id - Discussion UUID
 * @returns GovernanceDiscussion object
 * @throws GovernanceDiscussionApiError on failure
 */
export async function getGovernanceDiscussion(id: string): Promise<GovernanceDiscussion> {
  const base = getOracleBridgeUrl();
  const res = await authenticatedFetch(`${base}/api/discussions/${encodeURIComponent(id)}`, {
  });
  if (!res.ok) throw await mapGovernanceError(res);
  return res.json();
}

// =============================================================================
// Stage transition + contributor invites (bl-1046)
//
// Proposer-drives model: the PROPOSER advances the stage and invites
// contributors; the INVITEE accepts/declines their own invite. Backend lives
// off-chain in oracle-bridge (ADR-014 — a discussion is pre-proposal
// orchestration; only the eventual formal PROPOSAL is on-chain).
// =============================================================================

/** Response from POST /api/discussions/:id/stage. */
export interface AdvanceStageResponse {
  id: string;
  stage: DiscussionStage;
  updatedAt: number;
  participantCount: number;
  commentCount: number;
}

/** A single contributor/invitation row as returned by the contributors API. */
export interface DiscussionContributorRow {
  id: string;
  userId: string;
  status: 'pending' | 'accepted' | 'declined';
  invitedBy?: string;
  invitedAt: number;
  respondedAt?: number | null;
}

/** Response from GET /api/discussions/:id/contributors. */
export interface DiscussionContributorsResponse {
  proposerId: string;
  contributors: DiscussionContributorRow[];
}

/**
 * bl-1552: per-call opt-out from the shared auth layer's global "session expired"
 * redirect for the SUPPLEMENTARY contributors read.
 *
 * `authServiceClient.authenticatedFetch` turns any single 401 into a full
 * `handleSessionExpired()` → cross-origin `/login` bounce (authz-001-7b decision B —
 * deliberately refresh-free; NOT changed here). That is correct for a primary data
 * call, but `listDiscussionContributors` is a non-fatal enrichment read: a genuinely
 * logged-in member who deep-links / hard-refreshes a discussion detail page must not
 * be logged out because a supplementary sub-resource momentarily 401s.
 *
 * Supplying a no-op `onSessionExpired` OVERRIDES the default redirect for THIS call
 * only (see auth `handleSessionExpired(reason, returnUrl, onSessionExpired)`), so the
 * call simply returns the 401, `mapGovernanceError` throws `UNAUTHENTICATED`, and the
 * caller (`DiscussionDetailPage`) — which already wraps this read in a non-fatal
 * try/catch — renders the discussion without the contributors panel. The GLOBAL 401
 * policy in `authServiceClient` is untouched.
 */
const SUPPRESS_SESSION_EXPIRY_REDIRECT = { onSessionExpired: (): void => {} } as const;

/**
 * Advance a discussion to its next stage (proposer-only, quality-gate-gated
 * server-side). Throws a GovernanceDiscussionApiError on 403 (not proposer),
 * 422 (gates unmet), or 409 (already at the terminal stage).
 *
 * bl-1552: `POST /:id/stage` is `requireSessionAuth` (oracle-bridge
 * discussion-contributors.ts) → uses `authenticatedFetchWithBearer` so the
 * identity-service JWT is attached (the bare cookie-only fetch 401s cold).
 */
export async function advanceDiscussionStage(id: string): Promise<AdvanceStageResponse> {
  const base = getOracleBridgeUrl();
  const res = await authenticatedFetchWithBearer(
    `${base}/api/discussions/${encodeURIComponent(id)}/stage`,
    {
      method: 'POST',
    },
  );
  if (!res.ok) throw await mapGovernanceError(res);
  return res.json();
}

/**
 * List the contributors + invitations for a discussion.
 *
 * bl-1552: `GET /:id/contributors` is `requireSessionAuth` (oracle-bridge
 * discussion-contributors.ts). This was the @TC-DISC-016 logout trace:
 *   1. Attach the identity-service JWT via `authenticatedFetchWithBearer` (the bare
 *      cookie-only fetch sent no Bearer → 401 on cold-boot/deep-link).
 *   2. Await the single-flight session bootstrap first, so a deep-link cannot fire
 *      this auth-gated read ahead of the store being populated with the Bearer.
 *   3. Opt out of the global session-expiry redirect, so a residual 401 on this
 *      supplementary read degrades gracefully instead of logging the member out.
 */
export async function listDiscussionContributors(
  id: string,
): Promise<DiscussionContributorsResponse> {
  // (2) readiness gate: do not fire the auth-gated read until the bootstrap refresh
  // has populated the identity-jwt-store (or reached a definitive verdict).
  await runIdentityCodeBootstrap();
  const base = getOracleBridgeUrl();
  const res = await authenticatedFetchWithBearer(
    `${base}/api/discussions/${encodeURIComponent(id)}/contributors`,
    {},
    // (3) non-fatal: a 401 here must not trip the shared /login bounce.
    SUPPRESS_SESSION_EXPIRY_REDIRECT,
  );
  if (!res.ok) throw await mapGovernanceError(res);
  return res.json();
}

/**
 * Invite a contributor to a discussion by principal id (proposer-only).
 *
 * bl-1552: `POST /:id/contributors` is `requireSessionAuth` → Bearer-attaching fetch.
 */
export async function inviteDiscussionContributor(
  id: string,
  principalId: string,
): Promise<DiscussionContributorRow> {
  const base = getOracleBridgeUrl();
  const res = await authenticatedFetchWithBearer(
    `${base}/api/discussions/${encodeURIComponent(id)}/contributors`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ principalId }),
    },
  );
  if (!res.ok) throw await mapGovernanceError(res);
  return res.json();
}

/**
 * Accept or decline an invitation (invitee-only).
 *
 * bl-1552: `POST /:id/contributors/:invitationId/(accept|decline)` is
 * `requireSessionAuth` → Bearer-attaching fetch.
 */
export async function respondToDiscussionInvitation(
  id: string,
  invitationId: string,
  action: 'accept' | 'decline',
): Promise<DiscussionContributorRow> {
  const base = getOracleBridgeUrl();
  const res = await authenticatedFetchWithBearer(
    `${base}/api/discussions/${encodeURIComponent(id)}/contributors/${encodeURIComponent(invitationId)}/${action}`,
    { method: 'POST' },
  );
  if (!res.ok) throw await mapGovernanceError(res);
  return res.json();
}
